import { Icon, LinearLoader } from '@advanza/ui'
import TableList from 'components/TableList'
import { useCallback, useMemo } from 'react'
import { FilterRecord, FilterValue } from './ReportFilter'

export interface ColumnDefinition {
    key: string
    name: string
    width?: string
    sortable?: boolean
}

export interface ReportData {
    [key: string]: FilterValue
}

export interface Pagination {
    page: number
    limit: number
    total: number
    pages: number
}

interface ReportListProps {
    pages: Record<number, string[]>
    rows: Record<string, ReportData>
    isLoading: boolean
    pagination: Pagination | null
    filters: FilterRecord
    fetchData: (page: number) => void
    columns: ColumnDefinition[]
    renderRow: (id: string) => { cols: React.ReactNode[] }
    emptyMessage?: string
    loadingMessage?: string
    showPagination?: boolean
    sorting?: { field: string | null; direction: 'ASC' | 'DESC' | null }
    onSortChange?: (field: string, direction: 'ASC' | 'DESC') => void
}

const ReportList = (props: ReportListProps) => {
    const {
        pages,
        rows,
        isLoading,
        pagination,
        sorting,
        columns,
        renderRow,
        emptyMessage,
        loadingMessage,
        fetchData,
        onSortChange,
    } = props

    const handlePageChange = useCallback(
        (page: number) => {
            fetchData(page)
        },
        [fetchData],
    )

    const handleSortChange = useCallback(
        (newFilter: { sorting?: [string, string] }) => {
            if (newFilter.sorting && onSortChange) {
                const [field, direction] = newFilter.sorting

                // Check if the field is sortable
                const column = columns.find((col) => col.key === field)
                if (column && column.sortable === false) {
                    // Don't trigger sorting for non-sortable columns
                    return
                }

                onSortChange(field, direction as 'ASC' | 'DESC')
            }
        },
        [columns, onSortChange],
    )

    const definitions = useMemo(
        () =>
            columns.reduce(
                (acc, column) => {
                    acc[column.key] = { name: column.name }
                    return acc
                },
                {} as Record<string, { name: string }>,
            ),
        [columns],
    )

    // Use the pages prop directly from Redux state
    // pages is already in the correct format: { pageNumber: [rowIds] }

    if (isLoading && (!pages || Object.keys(pages).length === 0)) {
        return (
            <div className="text-center py-10">
                <LinearLoader />
                <p className="mt-4 text-grey-800">{loadingMessage || 'Loading report data...'}</p>
            </div>
        )
    }

    if (!isLoading && (!rows || Object.keys(rows).length === 0)) {
        return (
            <div className="text-center py-10">
                <Icon name="assessment" size={48} className="text-grey-500" />
                <p className="mt-4 text-grey-800">
                    {emptyMessage || 'No data found for the selected filters.'}
                </p>
            </div>
        )
    }

    return (
        <div className="mb-[100px]">
            <TableList
                definitions={definitions}
                rows={rows || {}}
                pages={pages || {}}
                count={pagination?.total || 0}
                isFetching={isLoading}
                addListFeatures
                hideSearch
                hideExport
                hideOrder={false}
                hidePagination
                renderRow={renderRow}
                filter={{
                    page: pagination?.page || 0,
                    pageSize: pagination?.limit || 50,
                    sorting: sorting?.field ? [sorting.field, sorting.direction || 'ASC'] : null,
                }}
                changeFilter={handleSortChange}
                onPageChange={handlePageChange}
                emptyMessage={emptyMessage}
            />
        </div>
    )
}

export default ReportList

// Re-export common renderers for convenience
export {
    renderProviderStatus,
    renderSubscriptionStatus,
    renderProspectValue,
    renderProviderId,
    renderDate,
    renderBoolean,
    renderText,
    renderCurrency,
} from './reportRenderers'
